<script setup>
import { ref, reactive, computed, watch, inject } from 'vue';
import axios from 'axios';
import FormCard from '@/components/FormCard.vue';
import QuantityInput from '@/components/QuantityInput.vue';
import { state, addGiftToBasket } from '@/shared/basketStore.js';
import { replacePanelContent } from '@/shared/panelStack.js';
import { formatPrice } from '@/shared/formatPrice.js';
import { perPersonPrice } from '@/shared/perPersonPrice.js';
import BasketPanel from '@/components/BasketPanel.vue';

const props = defineProps({
    product:            { type: Object, required: true },
    locations:          { type: Array,  default: () => [] },
    // Null until the customer picks one - see CalendarBookingPanel's note.
    locationId:         { type: Number, default: null },
    initialPeople:      { type: Number, default: null },
    giftVariantsApiUrl: { type: String, required: true },
});

// Only show locations where this package is actually offered - mirrors CalendarBookingPanel.
const availableLocations = computed(() =>
    props.product.location_ids?.length
        ? props.locations.filter(l => props.product.location_ids.includes(l.id))
        : props.locations
);

// The reassurance under the spa choice: the spa picked here sets the price, not where the
// gift can be used. Counted from the same list the select offers, so the number always
// matches what the customer can see. Withheld where only one spa sells the product, since
// "any of the 1 locations" is not a reassurance.
const showRedeemableHint = computed(() => availableLocations.value.length > 1);

// NEVER seeded, whatever the caller passes
// (docs/feature-requests/gift-location-free-search-scope-technical.md, decision 7).
//
// The spa is the first thing a gift asks, and it is asked out loud: a gift is redeemed by
// someone else, so the spa is a decision about the RECIPIENT rather than a continuation of
// the buyer's own browsing. Seeding it from a cookie or a search would collapse that
// question behind a summary naming a spa the buyer never chose for them, and the price
// gated on it (below) would appear already answered.
//
// Enforced here rather than by passing null from each of the three openers - the search
// results, the product page and the category listing - so a fourth cannot forget to. The
// `locationId` prop is deliberately left in place and deliberately ignored: removing it
// would silently change nothing at the call sites, whereas ignoring it is visible here.
const selectedLocationId = ref(null);
const selectedPeople      = ref(props.initialPeople ?? props.product.people ?? 1);
const selectedVariantId   = ref(props.product.id);
const quantity            = ref(1);
const adding              = ref(false);

// Which stages are collapsed - the panel owns this, mirroring CalendarBookingPanel.
//
// Location always starts open now, because it is never seeded: it is the question this
// panel leads with. Guests still arrives answered by the search, and the option stage
// opens once there is a spa to price its options against.
const stages = reactive({
    location: false,
    guests:   true,
    variant:  false,
});

// A gifted product isn't scheduled at purchase time (no calendar/time-slot picker, unlike
// the Book flow) - the recipient books their own slot later, at redemption. See
// buy-as-a-gift-scope-technical.md §9.
//
// 'spa-offer' is mapped alongside 'spa-day' for consistency with CalendarBookingPanel
// rather than because it can be reached: spa offers are not giftable, so no offer card
// ever opens this panel.
const apiType = computed(() => {
    if (props.product.type === 'spa-day' || props.product.type === 'spa-offer') return 'package';
    if (props.product.type === 'spa-break') return 'spa-break';
    return 'treatment';
});

// Day-range variants are Package-only (§8) - SpaBreak/Treatment never show this step. The
// PRICE is answered for all three, which is why this fetch is no longer package-only.
const variants = ref([]);
const variantsLoading = ref(false);
const variantsError = ref(null);

// The chosen spa's own price for the browsed product, straight from the server. Null until
// a spa is chosen, which is what gates the totals below.
//
// It is NOT taken from product.price_from, and that is the whole point of decision 6.
// price_from on a location-free search is globalPrice() - the cheapest branch anywhere -
// while the basket charges priceAt() for the spa actually chosen. Quoting the first and
// charging the second showed "£56" in this footer and put £62 in the basket, at the moment
// of commitment. The endpoint answers with the very call the basket makes, so the two
// cannot drift.
const locationPrice = ref(null);

/**
 * Both answers for one spa and guest count: what this product costs there, and which
 * day-range options it has. One request, because they are one question - "what am I
 * actually buying at this spa" - and because a second would let the price and the option
 * list describe different moments.
 */
async function fetchPriceAndVariants(people, locationId) {
    // Nothing to ask until a spa is chosen. Cleared rather than left standing, so changing
    // the spa cannot leave the previous one's price on screen while the next is in flight.
    variants.value = [];
    locationPrice.value = null;

    if (! locationId) return;

    variantsLoading.value = true;
    variantsError.value = null;

    try {
        const response = await axios.get(props.giftVariantsApiUrl, {
            params: {
                product_id: props.product.id,
                people,
                type: apiType.value,
                location_id: locationId,
            },
        });

        locationPrice.value = response.data.price;
        variants.value = response.data.variants;

        // Keep the current selection if it's still a valid option for this guest count,
        // otherwise fall back to whichever variant comes back first.
        if (!variants.value.some(v => v.id === selectedVariantId.value)) {
            selectedVariantId.value = variants.value[0]?.id ?? props.product.id;
        }
    } catch {
        variantsError.value = 'Could not load options. Please try again.';
        variants.value = [];
        locationPrice.value = null;
    } finally {
        variantsLoading.value = false;
    }
}

// Both inputs, not just the guest count: changing the spa re-prices everything, and until
// this watched the spa too a customer could pick a different one and keep the first one's
// figures. Immediate so the first render settles into its "no spa, no price" state through
// the same path as every later change.
watch(
    [selectedPeople, selectedLocationId],
    ([people, locationId]) => fetchPriceAndVariants(people, locationId),
    { immediate: true },
);

// The guest counts this product's family serves, as in CalendarBookingPanel.
const peopleOptions = computed(() =>
    props.product.people_options?.length
        ? props.product.people_options
        : [props.product.people ?? 1]
);

const guestLabel = (people) => (people === 1 ? '1 Guest' : `${people} Guests`);

// Only worth a card when there is more than one answer.
const showGuestPicker = computed(() =>
    props.product.type !== 'spa-break' && peopleOptions.value.length > 1
);

// A spa break's count lives on the record; a package's is whatever its family settled on.
const fixedPeople = computed(() =>
    props.product.type === 'spa-break' ? (props.product.people ?? 1) : selectedPeople.value
);

// Collapsed titles: the value each stage settled on. No location summary - the
// locations payload has no venue line in it.
const selectedLocationName = computed(() =>
    props.locations.find(l => l.id === selectedLocationId.value)?.name ?? null
);

const selectedVariantName = computed(() =>
    variants.value.find(v => v.id === selectedVariantId.value)?.name ?? null
);

// Only worth a card when there is more than one option.
const showVariantPicker = computed(() =>
    apiType.value === 'package' && ! variantsLoading.value && variants.value.length > 1
);

// How many more gifts the basket will take, from the basket payload. Null before the basket
// has been fetched, in which case the stepper has no ceiling of its own and the server's
// "cannot add more" refusal is the backstop.
const giftSlots = computed(() => state.basket.giftVoucherSlots ?? null);

const basketFull = computed(() => giftSlots.value === 0);

// Pulled back in if the basket fills up behind an open panel - another tab, or the basket
// panel's own remove undone - so the stepper never holds a number the add would refuse.
watch(giftSlots, (slots) => {
    if (slots !== null && quantity.value > slots) {
        quantity.value = Math.max(1, slots);
    }
});

const continuePayload = computed(() => {
    // No Add to Basket at all when the basket cannot take another gift: the quantity card
    // says why instead, rather than letting the press fail.
    if (!selectedLocationId.value || basketFull.value) return null;

    const payload = {
        product_id:  props.product.id,
        location_id: selectedLocationId.value,
        type:        apiType.value,
        // Each one becomes its own voucher and basket item (BasketController::storeGift()).
        quantity:    quantity.value,
    };

    if (selectedVariantId.value && selectedVariantId.value !== props.product.id) {
        payload.variant_product_id = selectedVariantId.value;
    }

    return payload;
});

// On success the panel's contents are swapped for the basket in place rather than a new
// panel stacking on top - the gift has been committed, so there is nothing here to come
// back to. Mirrors CalendarBookingPanel's onContinue().
async function onContinue() {
    if (!continuePayload.value || adding.value) return;

    adding.value = true;
    const added = await addGiftToBasket(continuePayload.value);
    adding.value = false;

    if (added) {
        replacePanelContent({ component: BasketPanel, title: 'Your Basket' });
    }
}

// The Add to Basket button belongs in the panel's sticky footer. Null outside a panel,
// in which case the teleport is disabled and it renders inline.
const panelFooter = inject('popOutPanelFooter', null);

// Footer totals, mirroring the server-rendered panel's footer
// (partials/booking-panel.blade.php) and CalendarBookingPanel's.
//
// The party total comes from the chosen variant where the variant picker is in play, since
// that is what the customer just priced up; otherwise from the endpoint's own figure for the
// browsed product. Unlike the Book flow there is no date here, so nothing else can move the
// price. Nullish coalescing rather than `||` so a genuinely free (£0) product is not
// mistaken for an absent price.
//
// There is deliberately NO fallback to product.price_from any more. That was the cheapest
// branch anywhere, and falling back to it is exactly how this panel used to quote a figure
// the basket would not honour - so with no spa chosen the honest answer is no figure at all.
const linePrice = computed(() => {
    const variantPrice = variants.value.find(v => v.id === selectedVariantId.value)?.price;

    return variantPrice ?? locationPrice.value ?? null;
});

const perPerson = computed(() => perPersonPrice(linePrice.value, selectedPeople.value));

// What Add to Basket will charge: one gift's price for each one bought.
const subtotal = computed(() => (linePrice.value === null ? null : linePrice.value * quantity.value));

// Gated on the spa (decision 6). No per-person rate, no subtotal and no Add to Basket until
// the customer has said where the gift is for, because until then there is no single price
// to show: the same product is £56 at one spa and £62 at another, and the basket will charge
// whichever they pick.
//
// Still withheld while the request is in flight, for the reason it always was: a figure that
// appears and is then replaced a moment later reads as a price change the customer did not
// cause.
const showTotals = computed(() =>
    selectedLocationId.value !== null
    && ! variantsLoading.value
    && linePrice.value !== null
);
</script>

<template>
    <FormCard
        title="Choose Location"
        icon="location"
        :completed="stages.location"
        :completed-title="selectedLocationName"
        @edit="stages.location = false"
    >
        <select
            v-model="selectedLocationId"
            :aria-describedby="showRedeemableHint ? `gift_location_hint_${product.id}` : null"
        >
            <!-- Only while nothing is chosen - mirrors CalendarBookingPanel. Without it the
                 select shows the first location while the model is still null, so the
                 customer believes they have picked one. -->
            <option v-if="selectedLocationId === null" :value="null" disabled>Choose a spa...</option>
            <option v-for="loc in availableLocations" :key="loc.id" :value="loc.id">
                {{ loc.name }}
            </option>
        </select>
        <p v-if="showRedeemableHint" :id="`gift_location_hint_${product.id}`" class="field_hint">
            Redeemable at any of the {{ availableLocations.length }} locations. Prices may vary by location.
        </p>
        <!-- Nothing to confirm until a location is chosen, as in CalendarBookingPanel. -->
        <button
            type="button"
            class="confirm_button"
            :disabled="selectedLocationId === null"
            @click="stages.location = true"
        >Confirm</button>
    </FormCard>

    <FormCard
        v-if="showGuestPicker"
        title="Number of Guests"
        icon="person"
        :completed="stages.guests"
        :completed-title="guestLabel(selectedPeople)"
        @edit="stages.guests = false"
    >
        <select v-model="selectedPeople">
            <option v-for="n in peopleOptions" :key="n" :value="n">
                {{ n }}
            </option>
        </select>
        <button type="button" class="confirm_button" @click="stages.guests = true">Confirm</button>
    </FormCard>

    <!-- A fixed guest count states itself rather than offering a card with a dead edit
         control: spa breaks carry the count on the record, and a package family may serve
         only one occupancy. -->
    <p v-else>
        For {{ fixedPeople === 1 ? '1 person' : `${fixedPeople} people` }}
    </p>

    <p v-if="variantsError" class="form_error">{{ variantsError }}</p>

    <!-- Day-range (or other) variant picker - only rendered when the family actually
         has more than one option for this guest count (decision #9 - listed by name
         and price only, no attempt to label which is "which" day range).
         The list is the design system's shared .check_list look (includes/form.scss),
         which supplies the column layout, row gap, top-aligned text and non-bold option
         text - so neither the label nor the input carries classes of its own. -->
    <FormCard
        v-if="showVariantPicker"
        title="Choose your option"
        :completed="stages.variant"
        :completed-title="selectedVariantName"
        @edit="stages.variant = false"
    >
        <div class="check_list">
            <label v-for="variant in variants" :key="variant.id">
                <!-- Completed on the change event rather than a watch, so that
                     fetchPriceAndVariants() resetting the selection for a new guest count
                     cannot collapse a stage the customer has not answered. -->
                <input
                    type="radio"
                    :name="`variant_${product.id}`"
                    :value="variant.id"
                    v-model="selectedVariantId"
                    @change="stages.variant = true"
                />
                <span class="check_list_option_name">{{ variant.name }}</span>
                <!-- Through the shared formatPrice, as the totals below do, so pence
                     show only when non-zero ("£74.50", "£59") and the figure matches
                     the server's <x-price> everywhere else on the page. -->
                <span v-if="variant.price" class="check_list_option_price">{{ formatPrice(variant.price) }}</span>
            </label>
        </div>
    </FormCard>

    <!-- Shown from the start, below the other cards, and with no Confirm: Add to Basket is
         what confirms it. -->
    <FormCard title="Quantity">
        <p v-if="basketFull" class="form_error">Sorry, you cannot add more gift vouchers to your basket.</p>
        <QuantityInput v-else v-model="quantity" label="Quantity" hide-label :max="giftSlots" />
    </FormCard>

    <p v-if="state.basketError" class="form_error">{{ state.basketError }}</p>

    <Teleport :to="panelFooter" :disabled="!panelFooter">
        <!-- Totals above the button, as in the server-rendered panel's footer. -->
        <table v-if="showTotals" class="summary_table">
            <tbody>
                <tr>
                    <th>Per Person</th>
                    <td>{{ formatPrice(perPerson) }}</td>
                </tr>
                <tr>
                    <th>Subtotal</th>
                    <td>{{ formatPrice(subtotal) }}</td>
                </tr>
            </tbody>
        </table>

        <button
            v-if="continuePayload"
            type="button"
            :disabled="adding || ! stages.location"
            class="button primary block"
            @click="onContinue"
        >
            {{ adding ? 'Adding…' : 'Add to Basket' }}
        </button>
    </Teleport>
</template>
